import React, { useState, useEffect } from 'react';

interface SidebarProps {
    children: React.ReactNode;
}

const Sidebar: React.FC<SidebarProps> = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [userName, setUserName] = useState<string>('');
    const [userRole, setUserRole] = useState<string>('');

    useEffect(() => {
        // Verifique o nome da chave exata que o seu authStore usa.
        // Com base no histórico, geralmente é 'gestjus_token' ou 'token'
        const token = localStorage.getItem('gestjus_token') || localStorage.getItem('token');

        if (token) {
            try {
                // Decodifica a carga útil (payload) do JWT que fica na segunda parte do token
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                }).join(''));

                const decoded = JSON.parse(jsonPayload);

                // O Spring Boot costuma salvar o login no 'sub' e as permissões num array 'roles' ou 'authorities'
                setUserName(decoded.nome || decoded.sub || 'Usuário');

                // Ajuste a busca da role conforme a estrutura do seu token Spring Security
                let role = 'ADM';
                if (decoded.roles && decoded.roles.length > 0) role = decoded.roles[0];
                else if (decoded.authorities && decoded.authorities.length > 0) role = decoded.authorities[0];

                // Limpa o prefixo 'ROLE_' se o Spring Security o adicionar
                setUserRole(role.replace('ROLE_', ''));

            } catch (e) {
                console.error("Erro ao decodificar o token JWT", e);
                setUserName('Usuário Logado');
                setUserRole('SISTEMA');
            }
        } else {
            setUserName('Administrador');
            setUserRole('ADM');
        }
    }, []);

    const fecharMenu = () => setIsOpen(false);

    return (
        <div className="flex min-h-screen bg-gray-100 font-sans">
            {/* TOPBAR MOBILE */}
            <div className="md:hidden flex items-center justify-between bg-[#1e3a8a] text-white p-4 w-full fixed top-0 z-30 shadow-md">
                <span className="font-bold text-xl tracking-wide">GestJus ⚖️</span>
                <button onClick={() => setIsOpen(true)} className="p-2 focus:outline-none focus:ring-2 focus:ring-blue-300 rounded">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                </button>
            </div>

            {/* OVERLAY MOBILE */}
            {isOpen && (
                <div className="fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity" onClick={fecharMenu}></div>
            )}

            {/* SIDEBAR PRINCIPAL */}
            <aside className={`fixed inset-y-0 left-0 w-64 bg-[#1e3a8a] text-white z-50 flex flex-col shadow-2xl transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>

                {/* Cabeçalho do Sidebar */}
                <div className="flex flex-col pt-6 pb-2 px-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-3xl font-extrabold tracking-tight">GestJus ⚖️</h2>
                            <p className="text-xs text-blue-300 uppercase tracking-widest mt-1">Gestão de Contratos</p>
                        </div>
                        <button onClick={fecharMenu} className="md:hidden p-2 text-blue-200 hover:text-white focus:outline-none">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Perfil do Usuário Logado */}
                <div className="px-6 py-4 mb-2 mx-4 mt-2 bg-blue-900/50 rounded-xl border border-blue-800/50 flex items-center gap-3 shadow-inner">
                    <div className="w-10 h-10 rounded-full bg-blue-700 flex items-center justify-center text-white font-bold text-lg border-2 border-blue-400 uppercase">
                        {userName.charAt(0)}
                    </div>
                    <div className="flex flex-col overflow-hidden">
                        <span className="text-sm font-bold text-white truncate leading-tight capitalize">{userName}</span>
                        <span className="text-[10px] text-blue-200 uppercase font-semibold tracking-wider mt-0.5">{userRole}</span>
                    </div>
                </div>

                {/* Links de Navegação */}
                <nav className="flex-1 px-4 py-2 space-y-2 overflow-y-auto">
                    <a href="/dashboard" onClick={fecharMenu} className="flex items-center gap-3 px-4 py-3 rounded-lg text-blue-100 hover:bg-blue-800 transition-colors">
                        📊 Dashboard
                    </a>
                    <a href="/contratos" onClick={fecharMenu} className="flex items-center gap-3 px-4 py-3 rounded-lg text-blue-100 hover:bg-blue-800 transition-colors">
                        📄 Contratos
                    </a>
                    <a href="/clientes" onClick={fecharMenu} className="flex items-center gap-3 px-4 py-3 rounded-lg text-blue-100 hover:bg-blue-800 transition-colors">
                        🏢 Clientes
                    </a>
                    <a href="/processos" onClick={fecharMenu} className="flex items-center gap-3 px-4 py-3 rounded-lg text-blue-100 hover:bg-blue-800 transition-colors">
                        ⚖️ Processos
                    </a>
                    <a href="/usuarios" onClick={fecharMenu} className="flex items-center gap-3 px-4 py-3 rounded-lg text-blue-100 hover:bg-blue-800 transition-colors">
                        👥 Usuários
                    </a>
                    <a href="/busca-ia" onClick={fecharMenu} className="flex items-center gap-3 px-4 py-3 rounded-lg text-blue-100 hover:bg-blue-800 transition-colors">
                        🧠 Busca Inteligente
                    </a>
                </nav>

                {/* Botão de Sair no Rodapé */}
                <div className="p-4 border-t border-blue-800">
                    <button
                        onClick={() => {
                            localStorage.clear();
                            window.location.href = '/';
                        }}
                        className="w-full flex justify-center items-center gap-2 bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-bold transition-colors shadow-md"
                    >
                        Sair do Sistema
                    </button>
                </div>
            </aside>

            {/* CONTEÚDO PRINCIPAL */}
            <main className="flex-1 w-full flex flex-col min-w-0 bg-gray-50 pt-16 md:pt-0">
                <div className="flex-1 p-6 md:p-8 overflow-y-auto">
                    {children}
                </div>
            </main>
        </div>
    );
};

export default Sidebar;